/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react'
import { toCamelCase } from '../component-helper'

/**
 * withSnakeCaseProps is a HOC for function components
 * it will return a React Component where all camelCase props gets converted to snake_case
 *
 * Use the same for TypeScript types by using: ToSnakeCase
 *
 * @param Base the original function or class
 * @returns extended function or class
 */
export function withSnakeCaseProps<TBase, P>(
  Base: ((props: P) => JSX.Element) & TBase
): typeof Base {
  const Component: React.ComponentType = Base

  const Derived = (props: P) => {
    return <Component {...convertSnakeCaseProps(props)} />
  }

  Object.defineProperty(Derived, 'name', {
    value: Base.name,
  })

  for (const name in Base) {
    Object.defineProperty(Derived, name, {
      value: Base[name],
    })
  }

  // @ts-expect-error - TypeScript doesn't understand dynamic class extension
  return Derived
}

/**
 * withSnakeCaseProps is a HOC for classes
 * it will return a React Component where all camelCase props gets converted to snake_case
 *
 * Use the same for TypeScript types by using: ToSnakeCase
 *
 * @param Base the original function or class
 * @returns extended function or class
 */
export function classWithSnakeCaseProps<
  TBase extends React.ComponentClass,
>(Base: TBase): typeof Base {
  const Component: React.ComponentClass = Base

  // Bug? https://github.com/microsoft/TypeScript/issues/37142
  // @ts-expect-error - Known TypeScript limitation with dynamic class extension
  class Derived extends Base {
    _prevProps: Record<string, unknown>
    _elem: React.ReactElement

    componentDidMount() {
      // ensures we do not run componentDidMount twice
    }

    render() {
      if (this.props !== this._prevProps) {
        this._prevProps = this.props
        this._elem = <Component {...convertSnakeCaseProps(this.props)} />
      }

      return this._elem
    }
  }

  Object.defineProperty(Derived, 'name', {
    value: Base.name,
  })

  Object.defineProperty(Derived, 'displayName', {
    value: Base.displayName || Base.name,
  })

  return Derived
}

export function convertSnakeCaseProps<P>(
  props: P,
  { overrideExistingValue = true } = {}
) {
  const isFrozen = Object.isFrozen(props)
  const newProps = isFrozen ? { ...props } : props

  for (const key in props) {
    if (key.includes('_') && /^[a-z]+/.test(key) && !/[A-Z]/.test(key)) {
      const newKey = toCamelCase(key)
      if (overrideExistingValue || newProps[newKey] === undefined) {
        newProps[toCamelCase(key)] = props[key]
      }
      delete newProps[key]
    }
  }

  return isFrozen ? Object.freeze(newProps) : newProps
}

/**
 * Convert recursively Types from camelCase to snake_case
 *
 * Use it like so:
 * OriginalProps & ToSnakeCase<OriginalProps>
 *
 * Disclaimer: Be careful using these with required props
 * - ToSnakeCase makes the required camelCase props also required in snake_case
 * - ToSnakeCasePartial removes required for the snake_case props
 *
 */
export type ToSnakeCasePartial<T> = Partial<ToSnakeCase<T>>
export type ToSnakeCase<T> = T extends object
  ? {
      [K in keyof T as ConvertCamelToSnakeCase<K & string>]:
        | T[K]
        | ToSnakeCase<T[K]>
    }
  : T
export type IncludeSnakeCase<T> = Partial<T> & ToSnakeCasePartial<T>
type ConvertCamelToSnakeCase<S extends string> =
  S extends `${infer T}${infer U}`
    ? `${T extends Capitalize<T>
        ? '_'
        : ''}${Lowercase<T>}${ConvertCamelToSnakeCase<U>}`
    : S
